import React, { useState, useEffect, useRef } from 'react';
import {
  Card, Button, Input, Form, Typography, Divider, Space,
  Tabs, Modal
} from 'antd';
import {
  WalletOutlined, MobileOutlined,
  QrcodeOutlined, ClockCircleOutlined, CheckCircleOutlined,
  CloseCircleOutlined, ArrowLeftOutlined,
  SafetyOutlined, HistoryOutlined, ExclamationCircleOutlined,
  LoadingOutlined, WarningOutlined
} from '@ant-design/icons';
import { postPayURequest } from '../../Utils/AppApi';
import { getUser } from '../../Utils/Common';
import { useHistory } from "react-router-dom";
import { toast } from 'react-toastify';
import QRCode from 'react-qr-code';
import UpiAtmHistory from '../reports/upiAtmHistory';
import 'react-toastify/dist/ReactToastify.css';

const { Title, Text } = Typography;
const { TabPane } = Tabs;

const PRIMARY_COLOR = '#C9262C';
const PRIMARY_LIGHT = '#fef0f0';
const QR_EXPIRY_SECONDS = 65;
const RETRY_INTERVAL_SECONDS = 15;
const MAX_RETRY_ATTEMPTS = 20;
const MAX_AMOUNT = 5000;

const UPI_ATM_GENERATE_QR = 'upi/instantpayatm/generateQr';
const UPI_ATM_CHECK_STATUS = 'upi/instantpayatm/checkStatus';

const QR_STATE = {
  INPUT: 'input',
  GENERATING: 'generating',
  ACTIVE: 'active',
  PENDING_RETRY: 'pending',
  TERMINAL: 'terminal'
};

const UpiAtm = () => {
  const history = useHistory();
  const [form] = Form.useForm();

  const qrTimerRef = useRef(null);
  const retryTimerRef = useRef(null);
  const redirectedRef = useRef(false);
  const retryAttemptsRef = useRef(0);

  const [activeTab, setActiveTab] = useState('generate');
  const [latitude, setLatitude] = useState("");
  const [longitude, setLongitude] = useState("");

  const [qrState, setQrState] = useState(QR_STATE.INPUT);
  const [formKey, setFormKey] = useState(0);

  const [currentStep, setCurrentStep] = useState(0);
  const [loading, setLoading] = useState(false);
  const [qrData, setQrData] = useState(null);
  const [timeLeft, setTimeLeft] = useState(QR_EXPIRY_SECONDS);
  const [checkingStatus, setCheckingStatus] = useState(false);
  const [amountInput, setAmountInput] = useState('');
  const [sessionSnapshot, setSessionSnapshot] = useState({ mobile: '', amount: '' });
  const [transactionAmount, setTransactionAmount] = useState(0);

  const [abandonModal, setAbandonModal] = useState(false);

  const quickAmounts = [500, 1000, 2000, 5000];

  // ---------- Location ----------
  useEffect(() => {
    const userData = getUser();
    const shopsLat = userData?.data?.shopsLat;
    const shopsLong = userData?.data?.shopsLong;
    if (shopsLat && Number(shopsLat) !== 0) {
      setLatitude(Number(shopsLat));
      setLongitude(Number(shopsLong));
    } else {
      navigator.geolocation.getCurrentPosition(
        (data) => { setLatitude(data.coords.latitude); setLongitude(data.coords.longitude); },
        () => { setLatitude("28.50937"); setLongitude("77.29727"); }
      );
    }
  }, []);

  // ---------- Cleanup ----------
  const clearTimers = () => {
    if (qrTimerRef.current) { clearInterval(qrTimerRef.current); qrTimerRef.current = null; }
    if (retryTimerRef.current) { clearInterval(retryTimerRef.current); retryTimerRef.current = null; }
  };

  useEffect(() => () => clearTimers(), []);

  // ---------- Helpers ----------
  const generateExternalRef = () => {
    const n = new Date();
    return `${n.getFullYear()}${(n.getMonth() + 1).toString().padStart(2, '0')}${n.getDate().toString().padStart(2, '0')}${n.getHours().toString().padStart(2, '0')}${n.getMinutes().toString().padStart(2, '0')}${n.getSeconds().toString().padStart(2, '0')}${Math.floor(Math.random() * 10000).toString().padStart(4, '0')}`;
  };

  const formatTime = (s) => `${Math.floor(s / 60).toString().padStart(2, '0')}:${(s % 60).toString().padStart(2, '0')}`;

  const handleAmountChange = (e) => {
    const value = e.target.value;
    if (value === '') {
      setAmountInput('');
      form.setFieldsValue({ amount: '' });
      return;
    }
    const num = parseFloat(value);
    if (isNaN(num)) return;
    if (num > MAX_AMOUNT) {
      setAmountInput(MAX_AMOUNT.toString());
      form.setFieldsValue({ amount: MAX_AMOUNT.toString() });
      toast.warning(`Maximum withdrawal amount is ₹${MAX_AMOUNT}`);
    } else {
      setAmountInput(value);
      form.setFieldsValue({ amount: value });
    }
  };

  // ---------- Navigate to invoice ----------
  const goToInvoice = (ipayId) => {
    if (redirectedRef.current) return;
    redirectedRef.current = true;
    clearTimers();
    setQrState(QR_STATE.TERMINAL);
    history.push(`/upi-atm-invoice/${ipayId}`);
  };

  // ---------- Reset (kept for safety; no UI trigger anymore) ----------
  const performFullReset = () => {
    clearTimers();
    redirectedRef.current = false;
    retryAttemptsRef.current = 0;

    setQrState(QR_STATE.INPUT);
    setCurrentStep(0);
    setQrData(null);
    setTimeLeft(QR_EXPIRY_SECONDS);
    setAmountInput('');
    setSessionSnapshot({ mobile: '', amount: '' });
    setTransactionAmount(0);
    setAbandonModal(false);

    setFormKey(k => k + 1);
    form.resetFields();
  };

  const handleGenerateNew = () => {
    if (qrState === QR_STATE.ACTIVE) {
      setAbandonModal(true);
      return;
    }
    performFullReset();
  };

  // ---------- Generate QR ----------
  const handleGenerateQR = async (values) => {
    if (qrState !== QR_STATE.INPUT) {
      toast.warning('A QR code is already active.');
      return;
    }

    const amountNum = parseFloat(values.amount);
    if (amountNum > MAX_AMOUNT) {
      toast.error(`Maximum withdrawal amount is ₹${MAX_AMOUNT}`);
      return;
    }

    setQrState(QR_STATE.GENERATING);
    setLoading(true);

    try {
      const payload = {
        mobile: values.mobile,
        amount: values.amount.toString(),
        externalRef: generateExternalRef(),
        devicetype: 'W',
        latitude: latitude.toString() || "28.50937",
        longitude: longitude.toString() || "77.29727"
      };

      const res = await postPayURequest(UPI_ATM_GENERATE_QR, payload, history);

      if (res.success && res.data) {
        redirectedRef.current = false;
        retryAttemptsRef.current = 0;

        setQrData(res.data);
        setTransactionAmount(parseFloat(values.amount));
        setSessionSnapshot({ mobile: values.mobile, amount: values.amount });
        setCurrentStep(1);
        setTimeLeft(res.data.displayExpirySec || QR_EXPIRY_SECONDS);
        setQrState(QR_STATE.ACTIVE);

        startQrCountdown(res.data.displayExpirySec || QR_EXPIRY_SECONDS, res.data.ipayId);
        toast.success('QR Code generated successfully');
      } else {
        setQrState(QR_STATE.INPUT);
        toast.error(res.message || 'Failed to generate QR code');
      }
    } catch (err) {
      setQrState(QR_STATE.INPUT);
      toast.error(err.message || 'Something went wrong');
    } finally {
      setLoading(false);
    }
  };

  // ---------- Countdown ----------
  const startQrCountdown = (seconds, ipayId) => {
    if (qrTimerRef.current) clearInterval(qrTimerRef.current);
    setTimeLeft(seconds);

    qrTimerRef.current = setInterval(() => {
      setTimeLeft(prev => {
        if (prev <= 1) {
          clearInterval(qrTimerRef.current);
          qrTimerRef.current = null;
          beginStatusPolling(ipayId);
          return 0;
        }
        return prev - 1;
      });
    }, 1000);
  };

  // ---------- Status polling ----------
  const beginStatusPolling = (ipayId) => {
    setQrState(QR_STATE.PENDING_RETRY);
    retryAttemptsRef.current = 0;
    runStatusCheckLoop(ipayId);
  };

  const runStatusCheckLoop = async (ipayId) => {
    if (redirectedRef.current) return;

    retryAttemptsRef.current += 1;

    if (retryAttemptsRef.current > MAX_RETRY_ATTEMPTS) {
      goToInvoice(ipayId);
      return;
    }

    setCheckingStatus(true);
    let terminal = false;

    try {
      const res = await postPayURequest(UPI_ATM_CHECK_STATUS, {
        ipayId,
        devicetype: 'W',
        latitude: latitude.toString() || "28.50937",
        longitude: longitude.toString() || "77.29727"
      }, history);

      if (res.success && res.data) {
        const st = res.data.qrStatus;
        if (st === 'SUCCESS' || st === 'FAILED' || st === 'EXPIRED') {
          terminal = true;
          goToInvoice(ipayId);
        }
      }
    } catch (_) {
      // keep looping
    } finally {
      setCheckingStatus(false);
    }

    if (terminal || redirectedRef.current) return;

    if (retryTimerRef.current) clearInterval(retryTimerRef.current);
    retryTimerRef.current = setTimeout(() => {
      retryTimerRef.current = null;
      runStatusCheckLoop(ipayId);
    }, RETRY_INTERVAL_SECONDS * 1000);
  };

  const handleQuickAmount = (amount) => {
    if (qrState !== QR_STATE.INPUT) return;
    setAmountInput(amount.toString());
    form.setFieldsValue({ amount: amount.toString() });
  };

  // ---------- Derived ----------
  const isFormLocked = qrState !== QR_STATE.INPUT;
  const showOverlay = qrState === QR_STATE.PENDING_RETRY || qrState === QR_STATE.TERMINAL;

  return (
    <div className="content-wrapper pt-2" style={{ padding: 0, margin: 0 }}>
      <style>{`
        .upi-atm-tabs .ant-tabs-nav { margin-bottom: 20px !important; }
        .upi-atm-tabs .ant-tabs-nav::before { border-bottom: 2px solid #f0f0f0 !important; }
        .upi-atm-tabs .ant-tabs-tab {
          padding: 10px 20px !important; font-size: 14px; font-weight: 500;
          color: #666; border-radius: 6px 6px 0 0 !important;
          background: transparent !important; border: none !important;
        }
        .upi-atm-tabs .ant-tabs-tab:hover { color: ${PRIMARY_COLOR} !important; }
        .upi-atm-tabs .ant-tabs-tab-active {
          background: #fff !important; border: 2px solid ${PRIMARY_COLOR} !important;
          border-bottom-color: #fff !important; color: ${PRIMARY_COLOR} !important;
          font-weight: 600 !important; margin-bottom: -2px !important;
        }
        .upi-atm-tabs .ant-tabs-ink-bar { display: none !important; }
        .upi-atm-hero {
          background: #fff; border: 1px solid #e8e8e8; border-radius: 12px;
          padding: 20px; display: flex; justify-content: space-between; align-items: center; margin-bottom: 16px;
        }
        .upi-atm-hero h3 { color: #1a1a1a !important; }
        .upi-atm-hero .ant-typography { color: #666 !important; }
        .upi-atm-info {
          background: #fafafa; border: 1px solid #e8e8e8; border-radius: 10px;
          padding: 10px 14px; display: flex; align-items: center; margin-bottom: 16px;
        }
        .upi-atm-input { border-radius: 8px !important; border-color: #d9d9d9 !important; }
        .upi-atm-input:hover { border-color: ${PRIMARY_COLOR}60 !important; }
        .upi-atm-input:focus, .ant-input-focused { border-color: ${PRIMARY_COLOR} !important; box-shadow: 0 0 0 2px ${PRIMARY_COLOR}15 !important; }
        .upi-atm-quick { display: flex; gap: 8px; flex-wrap: wrap; }
        .upi-atm-quick-btn {
          border-radius: 18px; border: 1px solid #d9d9d9;
          color: #555; background: #fff; font-weight: 500;
        }
        .upi-atm-quick-btn:hover:not(:disabled) { background: ${PRIMARY_LIGHT}; border-color: ${PRIMARY_COLOR}40; color: ${PRIMARY_COLOR}; }
        .upi-atm-gen-btn {
          height: 48px !important; border-radius: 24px !important;
          background: ${PRIMARY_COLOR} !important; border: none !important;
          font-size: 15px !important; font-weight: 600 !important;
        }
        .upi-atm-gen-btn:hover:not(:disabled) { background: #b52228 !important; }
        .upi-atm-qr-frame {
          display: inline-block; padding: 22px; background: #fff;
          border: 2px solid #e8e8e8; border-radius: 20px;
          box-shadow: 0 8px 32px rgba(0,0,0,0.08);
          animation: qrFadeIn 0.4s ease-out;
        }
        @keyframes qrFadeIn { from { opacity: 0; transform: scale(0.9); } to { opacity: 1; transform: scale(1); } }

        .upi-atm-timer-large {
          display: inline-flex; align-items: center; gap: 12px;
          padding: 14px 32px; border-radius: 40px;
          background: linear-gradient(135deg, #fff 0%, ${PRIMARY_LIGHT} 100%);
          border: 2px solid ${PRIMARY_COLOR}30;
          box-shadow: 0 6px 20px rgba(201,38,44,0.08);
          margin: 16px 0 4px 0;
          transition: all 0.3s ease;
        }
        .upi-atm-timer-large .timer-icon { font-size: 32px; color: ${PRIMARY_COLOR}; }
        .upi-atm-timer-large .timer-value {
          font-size: 34px; font-weight: 800; letter-spacing: 1px;
          color: ${PRIMARY_COLOR}; font-variant-numeric: tabular-nums;
        }
        .upi-atm-timer-large.warn {
          background: linear-gradient(135deg, #fff 0%, #fff2f0 100%);
          border-color: #ffccc7;
          animation: warnPulse 1s infinite;
        }
        .upi-atm-timer-large.warn .timer-icon,
        .upi-atm-timer-large.warn .timer-value { color: #cf1322; }
        @keyframes warnPulse {
          0%,100% { box-shadow: 0 6px 20px rgba(207,19,34,0.10); }
          50%     { box-shadow: 0 6px 32px rgba(207,19,34,0.28); }
        }

        .upi-atm-status-overlay {
          position: fixed; inset: 0; z-index: 9999;
          background: rgba(255,255,255,0.94);
          backdrop-filter: blur(6px);
          display: flex; flex-direction: column;
          align-items: center; justify-content: center;
          gap: 18px; text-align: center; padding: 24px;
          animation: overlayFade 0.25s ease-out;
        }
        @keyframes overlayFade { from { opacity: 0; } to { opacity: 1; } }
        .upi-atm-status-overlay .spin-icon {
          font-size: 72px; color: ${PRIMARY_COLOR};
          animation: spin 1.2s linear infinite;
        }
        @keyframes spin { from { transform: rotate(0deg); } to { transform: rotate(360deg); } }
        .upi-atm-status-overlay h3 {
          margin: 0 !important; font-size: 22px !important; font-weight: 700 !important;
          color: #1a1a1a !important;
        }
        .upi-atm-status-overlay p {
          margin: 0 !important; font-size: 14px; color: #666; max-width: 360px;
          line-height: 1.6;
        }
        .upi-atm-status-overlay .progress-dots { display: flex; gap: 8px; margin-top: 6px; }
        .upi-atm-status-overlay .progress-dots span {
          width: 10px; height: 10px; border-radius: 50%;
          background: ${PRIMARY_COLOR}; opacity: 0.25;
          animation: dotPulse 1.4s infinite;
        }
        .upi-atm-status-overlay .progress-dots span:nth-child(2) { animation-delay: 0.2s; }
        .upi-atm-status-overlay .progress-dots span:nth-child(3) { animation-delay: 0.4s; }
        @keyframes dotPulse {
          0%,100% { opacity: 0.25; transform: scale(1); }
          50%     { opacity: 1;    transform: scale(1.15); }
        }

        .upi-atm-warning-card {
          background: #fff7e6;
          border: 1.5px solid #ffd591;
          border-radius: 12px;
          padding: 16px 18px;
          margin-bottom: 16px;
          display: flex;
          gap: 12px;
          align-items: flex-start;
          box-shadow: 0 4px 14px rgba(212, 107, 8, 0.08);
        }
        .upi-atm-tips-card {
          background: #f0f5ff;
          border: 1px solid #adc6ff;
          border-radius: 12px;
          padding: 16px 18px;
          box-shadow: 0 4px 14px rgba(47, 84, 235, 0.06);
        }
      `}</style>

      <div className="content">
        <div className="container-fluid">
          <div className="row">
            <div className="col-md-12">
              <div className="card rounded-0">
                <div className="card-body">
                  <Tabs activeKey={activeTab} onChange={setActiveTab} className="upi-atm-tabs">

                    <TabPane tab={<span><QrcodeOutlined /> Generate QR</span>} key="generate">

                      {/* ---------- STEP 0: FORM ---------- */}
                      {currentStep === 0 && (
                        <div className="row">
                          <div className="col-md-6">
                            <div className="upi-atm-hero">
                              <div>
                                <Title level={3} style={{ color: '#1a1a1a', margin: 0, fontSize: 20 }}>
                                  UPI <span style={{ color: PRIMARY_COLOR, fontWeight: 700 }}>QR</span> Cash Withdrawal
                                </Title>
                                <Text style={{ color: '#666', fontSize: 13 }}>Scan • Pay • Withdraw Cash Instantly</Text>
                              </div>
                              <div style={{ width: 48, height: 48, borderRadius: '50%', background: PRIMARY_LIGHT, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                                <QrcodeOutlined style={{ fontSize: 24, color: PRIMARY_COLOR }} />
                              </div>
                            </div>
                            <div className="upi-atm-info">
                              <SafetyOutlined style={{ color: '#666', marginRight: 8 }} />
                              <Text style={{ fontSize: 12, color: '#888' }}>
                                Enter customer's mobile number and amount (max ₹{MAX_AMOUNT}) to generate QR
                              </Text>
                            </div>
                            <Form
                              key={formKey}
                              form={form}
                              layout="vertical"
                              onFinish={handleGenerateQR}
                              size="large"
                              disabled={isFormLocked}
                            >
                              <Form.Item
                                name="mobile"
                                label="Customer's Mobile Number"
                                rules={[{ required: true, message: 'Required' }, { pattern: /^\d{10}$/, message: 'Invalid mobile' }]}
                              >
                                <Input
                                  prefix={<MobileOutlined style={{ color: '#999' }} />}
                                  placeholder="10-digit mobile number"
                                  maxLength={10}
                                  className="upi-atm-input"
                                />
                              </Form.Item>
                              <Form.Item
                                name="amount"
                                label={`Withdrawal Amount (₹100 - ₹${MAX_AMOUNT})`}
                                rules={[
                                  { required: true, message: 'Required' },
                                  {
                                    validator: (_, v) => {
                                      const n = parseFloat(v);
                                      if (isNaN(n) || n < 100) return Promise.reject('Min ₹100');
                                      if (n > MAX_AMOUNT) return Promise.reject(`Max ₹${MAX_AMOUNT}`);
                                      return Promise.resolve();
                                    }
                                  }
                                ]}
                              >
                                <Input
                                  prefix={<WalletOutlined style={{ color: '#999' }} />}
                                  placeholder={`₹100 - ₹${MAX_AMOUNT}`}
                                  type="number"
                                  className="upi-atm-input"
                                  value={amountInput}
                                  onChange={handleAmountChange}
                                  max={MAX_AMOUNT}
                                />
                              </Form.Item>
                              <div style={{ marginBottom: 16 }}>
                                <Text style={{ fontSize: 12, color: '#999', marginBottom: 8, display: 'block' }}>Quick Amounts:</Text>
                                <div className="upi-atm-quick">
                                  {quickAmounts.map(a => (
                                    <Button
                                      key={a}
                                      size="small"
                                      onClick={() => handleQuickAmount(a)}
                                      className="upi-atm-quick-btn"
                                      disabled={isFormLocked}
                                    >
                                      ₹{a}
                                    </Button>
                                  ))}
                                </div>
                              </div>
                              <Button
                                type="primary"
                                htmlType="submit"
                                loading={loading}
                                disabled={isFormLocked}
                                icon={<QrcodeOutlined />}
                                block
                                className="upi-atm-gen-btn"
                              >
                                {qrState === QR_STATE.GENERATING ? 'Generating QR…' : 'Generate QR Code'}
                              </Button>
                            </Form>
                          </div>

                          <div className="col-md-6" style={{ display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                            <div style={{ textAlign: 'center', color: '#d9d9d9' }}>
                              <img src="../images/issuer_bank.png" alt="UPI ATM" style={{ maxWidth: '80%', opacity: 0.8 }} />
                            </div>
                          </div>
                        </div>
                      )}

                      {/* ---------- STEP 1: QR DISPLAY ---------- */}
                      {currentStep === 1 && qrState === QR_STATE.ACTIVE && (
                        <div className="row">

                          {/* LEFT: summary + timer + QR */}
                          <div className="col-md-6" style={{ textAlign: 'center' }}>
                            <div style={{
                              marginBottom: 4, padding: '10px 18px', borderRadius: 10,
                              background: PRIMARY_LIGHT, display: 'inline-block',
                              border: `1px solid ${PRIMARY_COLOR}30`
                            }}>
                              <Text strong style={{ color: PRIMARY_COLOR, fontSize: 16 }}>
                                ₹{sessionSnapshot.amount} &nbsp;•&nbsp; {sessionSnapshot.mobile}
                              </Text>
                            </div>

                            <div className={`upi-atm-timer-large ${timeLeft <= 10 ? 'warn' : ''}`}>
                              <ClockCircleOutlined className="timer-icon" />
                              <span className="timer-value">{formatTime(timeLeft)}</span>
                            </div>

                            {qrData?.qrString && (
                              <div style={{ marginTop: 12 }}>
                                <div className="upi-atm-qr-frame">
                                  <QRCode value={qrData.qrString} size={320} level="H" bgColor="#FFFFFF" fgColor="#000000" />
                                </div>
                              </div>
                            )}
                          </div>

                          {/* RIGHT: warning + tips */}
                          <div className="col-md-6">
                            <div className="upi-atm-warning-card">
                              <WarningOutlined style={{ color: '#d46b08', fontSize: 26, flexShrink: 0, marginTop: 2 }} />
                              <div>
                                <Text strong={{ color: '#d46b08' }} style={{ color: '#d46b08', fontSize: 15, display: 'block', marginBottom: 6 }}>
                                  Payment In Progress
                                </Text>
                                <Text style={{ color: '#874d00', fontSize: 13, lineHeight: 1.8 }}>
                                  Please do not press Back or close or refresh the page, until the payment status is confirmed.
                                  <br />
                                  Interrupting may cause transaction loss.
                                </Text>
                              </div>
                            </div>

                            <div className="upi-atm-tips-card">
                              <div style={{ display: 'flex', alignItems: 'center', marginBottom: 10 }}>
                                <SafetyOutlined style={{ color: '#2f54eb', fontSize: 20, marginRight: 8 }} />
                                <Text strong style={{ color: '#2f54eb', fontSize: 15 }}>How to pay</Text>
                              </div>
                              <ol style={{ margin: 0, paddingLeft: 20, color: '#1d39c4', fontSize: 13, lineHeight: 2 }}>
                                <li>Ask the customer to open any UPI app</li>
                                <li>Scan the QR code shown on the left</li>
                                <li>Verify the amount (<b>₹{sessionSnapshot.amount}</b>) and confirm</li>
                                <li>Wait here — the status will update automatically</li>
                              </ol>
                            </div>

                            <div style={{
                              marginTop: 16, padding: '10px 14px', borderRadius: 10,
                              background: timeLeft <= 10 ? '#fff2f0' : '#fffbe6',
                              border: `1px solid ${timeLeft <= 10 ? '#ffccc7' : '#ffe58f'}`
                            }}>
                              <ClockCircleOutlined style={{ color: timeLeft <= 10 ? '#cf1322' : '#d48806', marginRight: 6 }} />
                              <Text style={{ fontSize: 12, color: timeLeft <= 10 ? '#a8071a' : '#874d00' }}>
                                Please complete your transaction before the QR code expires
                              </Text>
                            </div>
                          </div>
                        </div>
                      )}

                    </TabPane>

                    <TabPane tab={<span><HistoryOutlined /> History</span>} key="history">
                      <UpiAtmHistory />
                    </TabPane>
                  </Tabs>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* ---------- Full-screen status overlay ---------- */}
      {showOverlay && (
        <div className="upi-atm-status-overlay">
          <LoadingOutlined className="spin-icon" />
          <Title level={3}>We're checking your payment status</Title>
          <p>Please wait — do not close this window. We'll take you to the invoice as soon as we get a result.</p>
          <div className="progress-dots"><span /><span /><span /></div>
        </div>
      )}

      {/* Abandon confirmation — kept in case it's needed elsewhere */}
      <Modal
        title={
          <span>
            <ExclamationCircleOutlined style={{ color: '#faad14', marginRight: 8 }} />
            Cancel this QR?
          </span>
        }
        open={abandonModal}
        onOk={performFullReset}
        onCancel={() => setAbandonModal(false)}
        okText="Yes, Generate New"
        okButtonProps={{ danger: true }}
        cancelText="Keep QR"
        maskClosable={false}
      >
        <p style={{ fontSize: 14, lineHeight: 1.6, margin: 0 }}>
          The current QR code for <b>₹{sessionSnapshot.amount}</b> ({sessionSnapshot.mobile}) will be closed.
          <br /><br />
          If the customer has already paid, the transaction may still be processed.
        </p>
      </Modal>
    </div>
  );
};

export default UpiAtm;